import { Mark, mergeAttributes } from "@tiptap/core";

export const APPLIED_TEXT_SIZE_CLASSES = ['md-text-small', 'md-text-large', 'md-text-xlarge'] as const;
export type AppliedTextSizeClass = (typeof APPLIED_TEXT_SIZE_CLASSES)[number];
export type TextSizeClass = AppliedTextSizeClass | 'md-text-normal';

export const DEFAULT_TEXT_SIZE_CLASS: TextSizeClass = 'md-text-normal';

export const TEXT_SIZE_LABELS: Record<TextSizeClass, string> = {
    'md-text-small': 'Klein',
    'md-text-normal': 'Normal',
    'md-text-large': 'Groß',
    'md-text-xlarge': 'Sehr groß',
};

export const isAppliedTextSizeClass = (value: unknown): value is AppliedTextSizeClass => {
    return (
        typeof value === 'string'
        && (APPLIED_TEXT_SIZE_CLASSES as readonly string[]).includes(value)
    );
};

export const resolveTextSizeClass = (value: unknown): TextSizeClass => {
    if (isAppliedTextSizeClass(value)) return value;
    return DEFAULT_TEXT_SIZE_CLASS;
};

declare module "@tiptap/core" {
    interface Commands<ReturnType> {
        textSize: {
            setTextSize: (sizeClass: AppliedTextSizeClass) => ReturnType;
            unsetTextSize: () => ReturnType;
        };
    }
}

export const TextSizeMark = Mark.create({
    name: 'textSize',

    addOptions() {
        return {
            HTMLAttributes: {},
        };
    },

    parseHTML() {
        return [
            {
                tag: 'span',
                getAttrs: (node) => {
                    if (!node || typeof node !== 'object') return false;
                    const className = (node as Element).getAttribute?.('class') ?? '';
                    const matchedClass = APPLIED_TEXT_SIZE_CLASSES.find((sizeClass) => className.split(/\s+/).includes(sizeClass));
                    return matchedClass ? { sizeClass: matchedClass } : false;
                },
            },
        ];
    },

    addAttributes() {
        return {
            sizeClass: {
                default: null,
                renderHTML: (attributes: { sizeClass?: string | null }) => {
                    if (!isAppliedTextSizeClass(attributes.sizeClass)) return {};
                    return { class: attributes.sizeClass };
                },
            },
        };
    },

    renderHTML({ HTMLAttributes }) {
        return ['span', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
    },

    renderMarkdown(node, helpers) {
        const content = node.content ?? [];
        const sizeClass = node.attrs?.sizeClass;
        if (!isAppliedTextSizeClass(sizeClass)) {
            return helpers.renderChildren(content);
        }

        return `<span class="${sizeClass}">${helpers.renderChildren(content)}</span>`;
    },

    addCommands() {
        return {
            setTextSize:
                sizeClass =>
                    ({ commands }) => {
                        return commands.setMark(this.name, { sizeClass });
                    },
            unsetTextSize:
                () =>
                    ({ commands }) => {
                        return commands.unsetMark(this.name);
                    },
        };
    },
});
